import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDS5Controller, LIGHTBAR_COLOR } from '../hooks/useDS5Controller';
import { useMovementCache } from '../hooks/useMovementCache';
import { api } from '../services/api';
import './ChargingStation.css';
import packageJson from '../../package.json';
import Cadance from './Cadance';

const APP_VERSION = packageJson.version;

const MOVEMENT_MAPPING = {
  "Charging": {
    "PASS": "Full Test",
    "FAIL": "Repair",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// ControllerCard — one per plugged-in DualSense
// ─────────────────────────────────────────────────────────────────────────────
const ControllerCard = ({ controller, movementsCache, userInfo, onHourIncrement, setControllerLightbar }) => {
  const { id: deviceKey, serialNumber, hasValidSerial, rawData, error } = controller;

  const [warning,             setWarning]             = useState('');
  const [showSuccess,         setShowSuccess]         = useState(false);
  const [nextJob,             setNextJob]             = useState('');
  const [isSaving,            setIsSaving]            = useState(false);
  const [currentMovementData, setCurrentMovementData] = useState(null);
  const [batteryLevel,        setBatteryLevel]        = useState(null);
  const [canShowBattery,      setCanShowBattery]      = useState(false);

  const hasAutoSavedRef      = useRef(false);
  const isCheckingRef        = useRef(false);
  const lastBatteryLevelRef  = useRef(null);
  const lastSerialRef        = useRef('');
  const movementCheckDoneRef = useRef(false);
  // ref instead of state: flip to true after successful save so the
  // battery effect can no longer change the color
  const savedRef             = useRef(false);
  // ref mirror of currentMovementData.nextjob — avoids stale closure in effects
  const nextJobRef           = useRef(null);

  const applyLightbar = useCallback((rgb) => {
    setControllerLightbar(deviceKey, ...rgb);
  }, [deviceKey, setControllerLightbar]);

  const decodeBatteryData = useCallback((batteryByte) => {
    if (batteryByte == null) return null;
    const levelCode      = batteryByte & 0x0F;
    const chargingStatus = (batteryByte >> 4) & 0x0F;
    const levelMap = {
      0x0: 5,  0x1: 15, 0x2: 25, 0x3: 35, 0x4: 45,
      0x5: 55, 0x6: 65, 0x7: 75, 0x8: 85, 0x9: 95, 0xA: 100,
    };
    return {
      level: levelMap[levelCode] !== undefined
        ? levelMap[levelCode]
        : Math.min(levelCode * 10 + 5, 100),
    };
  }, []);

  const getMovementNameById = useCallback((movementId) => {
    if (!movementsCache || !movementId) return null;
    for (const [name, mid] of Object.entries(movementsCache)) {
      if (mid === movementId) return name;
    }
    return null;
  }, [movementsCache]);

  // ── Parse battery from raw HID data ───────────────────────────────────────
  useEffect(() => {
    if (!rawData || rawData.length === 0) { setBatteryLevel(null); return; }

    let batteryByte = null;
    if (rawData.length > 52) {
      const b = rawData[52];
      if ((b & 0x0F) <= 0x0B && (b >> 4) <= 0x02) batteryByte = b;
    }
    if (!batteryByte && rawData.length > 53) batteryByte = rawData[53];
    if (!batteryByte) {
      for (let i = 40; i < rawData.length; i++) {
        if ((rawData[i] & 0x0F) <= 0x0F) { batteryByte = rawData[i]; break; }
      }
    }

    if (batteryByte !== null) {
      const bd = decodeBatteryData(batteryByte);
      if (bd) setBatteryLevel(bd.level);
    } else {
      setBatteryLevel(null);
    }
  }, [rawData, decodeBatteryData]);

  // ── Reset everything when a new controller is plugged in ──────────────────
  useEffect(() => {
    if (lastSerialRef.current === serialNumber) return;
    lastSerialRef.current        = serialNumber;
    hasAutoSavedRef.current      = false;
    movementCheckDoneRef.current = false;
    savedRef.current             = false;
    nextJobRef.current           = null;
    setShowSuccess(false);
    setNextJob('');
    setCurrentMovementData(null);
    setWarning('');
    setCanShowBattery(false);
    lastBatteryLevelRef.current  = null;
    // hook already sends WHITE on connect — reinforce it here too
    setControllerLightbar(deviceKey, ...LIGHTBAR_COLOR.WHITE);
  }, [serialNumber, deviceKey, setControllerLightbar]);

  // ── Invalid serial → WHITE ────────────────────────────────────────────────
  useEffect(() => {
    if (!hasValidSerial) applyLightbar(LIGHTBAR_COLOR.WHITE);
  }, [hasValidSerial, applyLightbar]);

  // ── Check movement history from DB ────────────────────────────────────────
  useEffect(() => {
    if (!hasValidSerial || !serialNumber || !movementsCache || Object.keys(movementsCache).length === 0) {
      setWarning('');
      setCurrentMovementData(null);
      setCanShowBattery(false);
      nextJobRef.current           = null;
      hasAutoSavedRef.current      = false;
      movementCheckDoneRef.current = false;
      return;
    }
    if (movementCheckDoneRef.current) return;

    const checkMovementHistory = async () => {
      if (isCheckingRef.current) return;
      isCheckingRef.current = true;
      try {
        const data = await api.getCurrentMovement(serialNumber);

        if (!data || (!data.id_movement && !data.id_nextjob)) {
          nextJobRef.current = '__UNKNOWN__';
          setWarning("Controller not in the database — Start with Quick Drift");
          hasAutoSavedRef.current = false;
          applyLightbar(LIGHTBAR_COLOR.WHITE);
          return;
        }

        const movementName  = getMovementNameById(data.id_movement);
        const nextJobName   = getMovementNameById(data.id_nextjob);
        const processedData = { ...data, movement: movementName, nextjob: nextJobName };

        // Update the ref BEFORE setState so the battery effect reads the right value
        nextJobRef.current = nextJobName;
        setCurrentMovementData(processedData);

        if (nextJobName === "Charging") {
          setCanShowBattery(true);
          setWarning('');
          // battery effect will set GREEN/RED once level is known
        } else {
          setCanShowBattery(false);
          setWarning(`Next_Job: ${nextJobName || 'None'}`);
          hasAutoSavedRef.current = false;
          applyLightbar(LIGHTBAR_COLOR.WHITE); // wrong next job → WHITE
        }
      } catch (err) {
        console.error("[DS5] Movement check error:", err);
        nextJobRef.current = '__ERROR__';
        setWarning("Erreur de vérification de l'historique");
        setCanShowBattery(false);
        applyLightbar(LIGHTBAR_COLOR.WHITE);
      } finally {
        isCheckingRef.current        = false;
        movementCheckDoneRef.current = true;
      }
    };

    checkMovementHistory();
  }, [hasValidSerial, serialNumber, movementsCache, getMovementNameById, applyLightbar]);

  // ── Live lightbar: GREEN ≥50% / RED <50% — ONLY when next job IS "Charging" ─
  //
  // Uses nextJobRef (a plain ref) instead of currentMovementData (React state)
  // so this effect always reads the most recent value — no stale closure issue.
  // savedRef prevents overriding the locked color after auto-save.
  //
  useEffect(() => {
    if (batteryLevel === null) return;
    if (nextJobRef.current !== "Charging") return; // wrong job → stay WHITE
    if (savedRef.current) return;                   // already saved → color locked
    applyLightbar(batteryLevel >= 50 ? LIGHTBAR_COLOR.GREEN : LIGHTBAR_COLOR.RED);
  }, [batteryLevel, applyLightbar]);

  // ── Auto-save once battery is stable ─────────────────────────────────────
  useEffect(() => {
    if (
      !hasValidSerial || !serialNumber ||
      batteryLevel === null || !currentMovementData ||
      !movementsCache || Object.keys(movementsCache).length === 0 ||
      isSaving || hasAutoSavedRef.current ||
      currentMovementData.nextjob !== "Charging"
    ) return;

    if (!userInfo?.id) { setWarning("Aucun opérateur connecté."); return; }

    const isBatteryStable =
      lastBatteryLevelRef.current === null ||
      Math.abs(batteryLevel - lastBatteryLevelRef.current) < 5;

    if (!isBatteryStable) { lastBatteryLevelRef.current = batteryLevel; return; }
    lastBatteryLevelRef.current = batteryLevel;

    const performAutoSave = async () => {
      setIsSaving(true);
      hasAutoSavedRef.current = true;
      try {
        const action        = batteryLevel >= 50 ? "PASS" : "FAIL";
        const nextJobTarget = MOVEMENT_MAPPING["Charging"][action];
        const movementId    = movementsCache["Charging"];
        const nextJobId     = movementsCache[nextJobTarget];

        if (!movementId || !nextJobId) throw new Error('IDs non trouvés dans le cache');

        await api.saveMovement({
          serial_number: serialNumber,
          operator:      userInfo.id,
          tester:        "Charging",
          movement:      movementId,
          nextjob:       nextJobId,
          Grade:         null,
          version:       APP_VERSION,
        });

        onHourIncrement();
        setNextJob(nextJobTarget);
        setShowSuccess(true);
        savedRef.current = true; // lock — battery effect will no longer override color

        applyLightbar(action === "PASS" ? LIGHTBAR_COLOR.GREEN : LIGHTBAR_COLOR.RED);
      } catch (err) {
        console.error("[DS5] Auto-save error:", err);
        hasAutoSavedRef.current = false;
        setWarning(`Erreur enregistrement: ${err.message}`);
        applyLightbar(LIGHTBAR_COLOR.WHITE);
      } finally {
        setIsSaving(false);
      }
    };

    const safetyTimeout = setTimeout(() => {
      hasAutoSavedRef.current = false;
      setIsSaving(false);
    }, 10_000);

    const timer = setTimeout(performAutoSave, 2_000);
    return () => { clearTimeout(timer); clearTimeout(safetyTimeout); };
  }, [
    hasValidSerial, serialNumber, batteryLevel, currentMovementData,
    movementsCache, isSaving, userInfo, onHourIncrement, applyLightbar,
  ]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="controller-card">
      <div className="serial-display">
        <label>Serial Number:</label>
        <div className="serial-value">{serialNumber || '—'}</div>
      </div>

      {!hasValidSerial && error && (
        <div className="error">{error}</div>
      )}

      {canShowBattery && (
        <div className="battery-display">
          <label>Niveau de Batterie:</label>
          <div className={`battery-value ${batteryLevel >= 50 ? 'good' : 'low'}`}>
            {batteryLevel !== null ? `${batteryLevel}%` : 'Lecture...'}
          </div>
        </div>
      )}

      {warning && <div className="warning-message">{warning}</div>}

      {canShowBattery && currentMovementData?.nextjob === "Charging"
        && isSaving && !showSuccess && batteryLevel !== null && userInfo && (
        <div className="waiting-message">Enregistrement en cours...</div>
      )}

      {showSuccess && nextJob && (
        <div className={`success-message ${nextJob === "Repair" ? 'fail' : ''}`}>
          <strong>{nextJob === "Full Test" ? 'PASS' : 'FAIL'}</strong><br />
          Batterie: {batteryLevel}% → Next Job: <span className="next-job">{nextJob}</span>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ChargingStation
// ─────────────────────────────────────────────────────────────────────────────
const ChargingStation = ({ userInfo, userPosition }) => {
  const { controllers, isWebHIDSupported, setControllerLightbar } = useDS5Controller();
  const movementsCache = useMovementCache();
  const [hourCount, setHourCount] = useState(0);

  const isCorrectStation = userPosition === "Charging";

  useEffect(() => {
    const now = new Date();
    const msUntilNextHour =
      (60 - now.getMinutes()) * 60_000 -
      now.getSeconds() * 1_000 -
      now.getMilliseconds();

    const timeoutId = setTimeout(() => {
      setHourCount(0);
      const intervalId = setInterval(() => setHourCount(0), 60 * 60_000);
      return () => clearInterval(intervalId);
    }, msUntilNextHour);

    return () => clearTimeout(timeoutId);
  }, []);

  const handleHourIncrement = useCallback(() => setHourCount(p => p + 1), []);

  if (!isCorrectStation) return null;

  return (
    <div className="charging-container">
      <Cadance userInfo={userInfo} movementId={2} hourCount={hourCount} />

      <div className="charging-content">
        <div className="charging-header">
          <h1>Charging</h1>
        </div>

        {!isWebHIDSupported && (
          <div className="error">WebHID not supported in this browser.</div>
        )}

        {isWebHIDSupported && controllers.length === 0 && (
          <div className="disconnected">
            <h1>🔌 Plug in your DualSense controller(s).</h1>
          </div>
        )}

        <div className="controllers-grid">
          {controllers.map(controller => (
            <ControllerCard
              key={controller.id}
              controller={controller}
              movementsCache={movementsCache}
              userInfo={userInfo}
              onHourIncrement={handleHourIncrement}
              setControllerLightbar={setControllerLightbar}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ChargingStation;